import type { ServiceSignInResult, SocialProvider } from '../../types';
import { PrivateEmailExposedError } from '../../types';
import type { IdentityProvider } from '../identity-provider';
import type { HttpClient } from '../client-app/index';
import { tokenExchange, TokenExchangeRegistrationFields } from './token-exchange';
import type { AuthLogger } from '../../shared/auth-logger';
import { SocialFlowStatus } from '../../constants';
import { isPrivateRelayEmail } from '../../shared/private-relay';

const SOCIAL_PROVIDER_MAP: Record<SocialProvider, 'Google' | 'Facebook' | 'Apple'> = {
  google: 'Google',
  facebook: 'Facebook',
  apple: 'Apple',
};

export async function cognitoSignInWithSocial(
  idp: IdentityProvider,
  provider: SocialProvider,
  customState?: string,
): Promise<void> {
  await idp.socialRedirect(SOCIAL_PROVIDER_MAP[provider], customState);
}

export async function cognitoAuthenticateAfterRedirect(
  idp: IdentityProvider,
  http: HttpClient,
  provider: SocialProvider,
  logger?: AuthLogger,
): Promise<ServiceSignInResult> {
  logger?.log(SocialFlowStatus.AuthenticateAfterRedirect, { provider });
  const idToken = await idp.getIdToken();

  const tokenProfile = await idp.getTokenProfile();
  let registrationFields: TokenExchangeRegistrationFields | undefined;

  const { firstName, lastName } = tokenProfile;
  if (firstName || lastName) {
    registrationFields = { profile: { firstName: firstName ?? '', lastName: lastName ?? '' } };
    logger?.log(SocialFlowStatus.ProfileForwarded, { provider, hasFirstName: Boolean(firstName), hasLastName: Boolean(lastName) });
  }

  const result = await tokenExchange(http, idToken, registrationFields, logger);
  if (result.needsPhone) {
    const profile = (firstName || lastName)
      ? { firstName: firstName ?? '', lastName: lastName ?? '' }
      : undefined;
    return { needsPhone: true, profile };
  }
  if (!result.user) {
    throw new Error('Unable to complete social authentication: missing user.');
  }

  if (provider === 'apple') {
    await enforceEmailPrivacy(idp, result.user.email, logger);
  }

  return { user: result.user };
}

async function enforceEmailPrivacy(
  idp: IdentityProvider,
  responseEmail: string,
  logger?: AuthLogger,
): Promise<void> {
  const tokenEmail = await idp.getTokenEmail();
  if (!tokenEmail || !isPrivateRelayEmail(tokenEmail)) return;

  if (tokenEmail.toLowerCase() !== responseEmail.toLowerCase()) {
    logger?.log(SocialFlowStatus.PrivateEmailExposed, { tokenEmail, responseEmail });
    throw new PrivateEmailExposedError();
  }
}
